import { Component, forwardRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { StammdatenortschaftService } from '../../../core/stammdaten-api/api/stammdatenortschaft.service';
import { Subscription } from 'rxjs';
import { OrtschaftListEntry } from '../../../core/stammdaten-api';
import { NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { AbstractTypeaheadSearch } from '../../components/typeahead-search/abstract-typeahead-search';
import { MetaDisablerDirective } from '../../directives/meta-disabler.directive';
import { MetaValidatorDirective } from '../../directives/meta-validator.directive';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'cap-ortschaft-selection',
  templateUrl: './ortschaft-selection.component.html',
  styleUrls: ['./ortschaft-selection.component.scss'],
  host: {
    class: 'form-control-component'
  },
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => OrtschaftSelectionComponent), multi: true }],
  imports: [FormsModule, NgbTypeahead, MetaDisablerDirective, MetaValidatorDirective, TranslatePipe]
})
export class OrtschaftSelectionComponent
  extends AbstractTypeaheadSearch<OrtschaftListEntry>
  implements OnInit, ControlValueAccessor, OnDestroy
{
  ortschaftListEntryVms: Array<OrtschaftListEntryVm>;
  ortschaftModel: OrtschaftListEntry;

  propagateChange: (id: string) => void;
  propagateTouch: (_) => void;

  private reset = false;
  private subscription: Subscription;

  @ViewChild(NgbTypeahead) ngbTypeahead: NgbTypeahead;

  constructor(private ortschaftService: StammdatenortschaftService) {
    super();
    this.minSearchLength = 0;
  }

  ngOnInit() {
    const findAll = this.ortschaftService.loadAll();
    findAll.subscribe((ortschaften) => {
      this.createViewModel(ortschaften);
    });
    this.setSearch(findAll);
  }

  private createViewModel(ortschaften: OrtschaftListEntry[]) {
    this.ortschaftListEntryVms = new Array<OrtschaftListEntryVm>();
    for (const ortschaft of ortschaften) {
      const entry = new OrtschaftListEntryVm(ortschaft);
      this.ortschaftListEntryVms.push(entry);
    }
  }

  writeValue(ortschaftVersionId: string): void {
    if (ortschaftVersionId) {
      this.subscription = this.ortschaftService.load1(ortschaftVersionId).subscribe((ortschaft) => {
        const ortschaftListEntry = this.ortschaftListEntryVms.find((vm) => vm.object.versionId.id === ortschaft.versionId.id).object;
        if (ortschaft.plz != null) {
          this.value = ortschaft.name + ' ' + ortschaft.plz;
          this.selectedItem = ortschaftListEntry;
          this.ortschaftModel = ortschaftListEntry;
        } else {
          this.value = ortschaft.name;
          this.selectedItem = null;
          this.ortschaftModel = null;
        }
        this.reset = false;
      });
    } else if (!this.reset) {
      this.onClear();
      this.reset = true;
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  onFocusField(searchInput: HTMLInputElement) {
    if (!this.ngbTypeahead.isPopupOpen()) {
      this.openTypeahead(searchInput);
    }
  }

  onSelected($event: NgbTypeaheadSelectItemEvent<OrtschaftListEntry>) {
    const selectedItem: OrtschaftListEntry = $event.item;
    if (selectedItem) {
      this.ortschaftModel = selectedItem;
      this.value = this.formatEntry(selectedItem);
      this.propagateChange(selectedItem.versionId.id);
      this.reset = false;
    } else {
      this.ortschaftModel = null;
      this.value = '';
      this.propagateChange(null);
    }
  }

  formatEntry(entry: OrtschaftListEntry): string {
    return entry.name + ' ' + entry.plz;
  }

  onClear() {
    super.onClear();
    this.ortschaftModel = null;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}

class OrtschaftListEntryVm {
  public plzAsString: string;
  public name: string;

  constructor(public object: OrtschaftListEntry) {
    this.plzAsString = object.plz === null ? '' : '' + object.plz;
    this.name = object.name;
  }
}
